import { useFormikContext } from 'formik';
import { FormikCheckboxField } from 'lib/components/formik/FormikCheckbox';
import { Gap } from 'lib/components/styles/layout';
import {
  ParagraphExtraSmall,
  ParagraphSmall,
} from 'lib/components/styles/typography';
import React, { useEffect, useState } from 'react';
import styled, { useTheme } from 'styled-components/macro';
import { ILandingPageBuilderForm } from '../../types';
import { DarkLabel } from './styles';
import { Search } from 'lib/components';
import SkeletonLoader from './SkeletonLoader';
import { Button } from 'react-covideo-common';
import { MdRefresh } from 'react-icons/md';
import { Customer } from 'lib/api/superadmin/customersQuery';

const CustomerName = styled.div`
  width: 250px;
  text-overflow: ellipsis;
  white-space: nowrap;
  overflow: hidden;
`;

type ScrollableContainerProps = {
  isResellerAdmin?: boolean;
};

const ScrollableContainer = styled.div<ScrollableContainerProps>`
  width: 100%;
  gap: 20px;
  display: flex;
  flex-direction: column;
  height: ${props => (props.isResellerAdmin ? '50vh' : '295px')};
  overflow-y: auto;
  margin-bottom: 25px;
  box-sizing: border-box;
`;

type CustomerAccessProps = {
  resellerId: number;
  isShareWithResellerSelected: boolean;
  customers: Customer[];
  isLoading: boolean;
  isFetching: boolean;
  displayedCount: number;
  count: number;
  isResellerAdmin: boolean;
  setCustomers: (arg: Customer[]) => void;
  setPage: React.Dispatch<React.SetStateAction<number>>;
  setSearch: (arg: string) => void;
  onToggleResellerFlag: () => void;
};

interface CustomerAccessObject {
  [customerId: number]: {
    default: number;
  };
}

const INIT_SIZE = 50;

const CustomerAccess = ({
  resellerId,
  isShareWithResellerSelected,
  customers,
  isLoading,
  isFetching,
  displayedCount,
  count,
  isResellerAdmin,
  setCustomers,
  setPage,
  setSearch,
  onToggleResellerFlag,
}: CustomerAccessProps) => {
  const theme = useTheme();
  const { values, setFieldValue } = useFormikContext<ILandingPageBuilderForm>();
  const [isSelectAllChecked, setIsSelectAllChecked] = useState(false);

  useEffect(() => {
    onShareWithResellerToggled();
  }, [isShareWithResellerSelected]);

  const onShareWithResellerToggled = () => {
    if (!isShareWithResellerSelected) return;
    if (isSelectAllChecked) {
      // uncheck
      setIsSelectAllChecked(false);
    }

    // update formik
    setFieldValue('access', {
      ...values.access,
      [resellerId]: {
        ...values.access[resellerId],
        selectedCustomers: {},
      },
    });
  };

  const onUpdateCustomerAccess = (
    newSelectedCustomers: CustomerAccessObject,
    shareWithResellerEnabledAndSelectAll?: boolean
  ) => {
    // formik update goes here
    setFieldValue('access', {
      ...values.access,
      [resellerId]: {
        ...values.access[resellerId],
        ...(shareWithResellerEnabledAndSelectAll && {
          isResellerSelected: false,
        }),
        selectedCustomers: newSelectedCustomers,
      },
    });
  };

  const toggleCustomer = (customerId: number) => {
    const newSelectedCustomers = values.access[resellerId].selectedCustomers;
    const exists = !!newSelectedCustomers[customerId];
    if (exists) {
      // remove
      delete newSelectedCustomers[customerId];
    } else {
      // add
      newSelectedCustomers[customerId] = {
        default: 0,
      };
    }

    onUpdateCustomerAccess(newSelectedCustomers);
  };

  const toggleDefault = (customerId: number) => {
    const newSelectedCustomers = values.access[resellerId].selectedCustomers;
    const exists = !!newSelectedCustomers[customerId];
    if (exists) {
      // toggle default
      newSelectedCustomers[customerId].default = newSelectedCustomers[
        customerId
      ].default
        ? 0
        : 1;
    } else {
      // add
      newSelectedCustomers[customerId] = {
        default: 1,
      };
    }

    onUpdateCustomerAccess(newSelectedCustomers);
  };

  const checkIfCustomerIsSelected = (customerId: number) => {
    return !!values.access[resellerId]?.selectedCustomers[customerId];
  };

  const checkIfCustomerIsUsingLandingPageAsDefault = (customerId: number) => {
    const exists = values.access[resellerId]?.selectedCustomers[customerId];
    if (!exists) return false;
    return !!exists.default;
  };

  const selectAllHandler = () => {
    const prevSelectAllValue = isSelectAllChecked;
    const newSelectAllValue = !prevSelectAllValue;
    setIsSelectAllChecked(newSelectAllValue);

    const shareWithResellerEnabledAndSelectAll =
      newSelectAllValue && isShareWithResellerSelected;

    if (shareWithResellerEnabledAndSelectAll) {
      // uncheck reseller flag
      onToggleResellerFlag();
    }
    if (!customers.length) return;

    // add or remove all currently visible customers
    if (!newSelectAllValue) {
      onUpdateCustomerAccess({});
      return;
    }

    // select all
    const newSelectedCustomers = customers.reduce(
      (acc, curr) => ({
        [curr.customerId]: {
          default:
            values.access[resellerId].selectedCustomers[curr.customerId]
              ?.default || 0,
        },
        ...acc,
      }),
      {}
    );

    onUpdateCustomerAccess(
      newSelectedCustomers,
      shareWithResellerEnabledAndSelectAll
    );
  };

  const handleSearch = (value: string) => {
    setCustomers([]);
    setPage(0);
    setSearch(value);
  };

  const isDataLoading = isFetching || isLoading;

  return (
    <Gap width='100%'>
      <Search
        width='100%'
        placeholder='Search customers by business name, ID or email'
        onSearch={value => {
          handleSearch(value);
        }}
        disabled={isDataLoading}
      />
      {!isLoading && (
        <>
          <Gap>
            <FormikCheckboxField
              name={`all-customers`}
              checked={isSelectAllChecked}
              disabled={isFetching}
              onChange={selectAllHandler}
              extendStyles={{
                wrapper: {
                  gap: '5px',
                },
                labelStyles: {
                  color: theme.colors.neutral[80],
                  fontSize: '14px',
                  fontWeight: 600,
                  marginLeft: '5px',
                },
              }}
              label='Select All'
            />
          </Gap>

          <Gap flexDirection='row' justifyContent='space-between' width='100%'>
            <DarkLabel style={{ width: '250px' }}>Business</DarkLabel>
            <Gap
              width='200px'
              justifyContent='space-between'
              flexDirection='column'
            >
              <DarkLabel>Assign Template</DarkLabel>
            </Gap>
            <Gap
              width='200px'
              justifyContent='space-between'
              flexDirection='column'
            >
              <DarkLabel>Set as default</DarkLabel>
            </Gap>
          </Gap>
        </>
      )}

      {isLoading ? (
        <SkeletonLoader />
      ) : (
        <ScrollableContainer isResellerAdmin={isResellerAdmin}>
          {customers.map(customer => {
            return (
              <Gap
                key={`customer-${customer.customerId}`}
                justifyContent='space-between'
              >
                <Gap
                  gap='5px'
                  flexDirection='column'
                  width='fit-content'
                  alignItems='flex-start'
                >
                  <CustomerName>{customer.business || '-'}</CustomerName>
                  <ParagraphExtraSmall color={theme.colors.neutral[60]}>
                    ID: {customer.customerId}
                  </ParagraphExtraSmall>
                </Gap>
                <Gap width='200px' justifyContent='center'>
                  <FormikCheckboxField
                    name={`customer-${customer.customerId}`}
                    checked={
                      isShareWithResellerSelected ||
                      checkIfCustomerIsSelected(customer.customerId)
                    }
                    disabled={isShareWithResellerSelected}
                    onChange={e => {
                      e.stopPropagation();
                      toggleCustomer(customer.customerId);
                    }}
                  />
                </Gap>
                <Gap width='200px' justifyContent='center'>
                  <FormikCheckboxField
                    name={`customer-default-${customer.customerId}`}
                    checked={checkIfCustomerIsUsingLandingPageAsDefault(
                      customer.customerId
                    )}
                    onChange={e => {
                      e.stopPropagation();
                      toggleDefault(customer.customerId);
                    }}
                  />
                </Gap>
              </Gap>
            );
          })}
          {displayedCount < count ? (
            <Gap
              width='100%'
              justifyContent='center'
              style={{ marginBottom: '25px' }}
            >
              <Button
                icon={<MdRefresh />}
                loading={isDataLoading}
                variant='secondary'
                text='Load More'
                size='small'
                onClick={() => setPage(prev => prev + INIT_SIZE)}
                disabled={isDataLoading}
              />
            </Gap>
          ) : (
            <ParagraphSmall>You've reached the end!</ParagraphSmall>
          )}
        </ScrollableContainer>
      )}
    </Gap>
  );
};

export default CustomerAccess;
